import { Link } from "react-router-dom";
import { ArrowUpRight, MessageCircle } from "lucide-react";
import { clients, contact, industries, officeProcess, partner, projectDesks } from "@kanata/shared";
import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/sections/PageHero";
import { SectionHeading } from "@/components/sections/SectionHeading";
import { TrustedBy } from "@/components/sections/TrustedBy";
import { Button } from "@/components/ui/button";
import { clientImages, clientsHeroImage, img } from "@/lib/images";

/**
 * Office projects.
 *
 * Proof of work, not a lead-capture funnel. There is deliberately no quote
 * form: an office fit-out starts with questions about the floor, the headcount
 * and the timeline that no form can ask well, so the CTA is a conversation.
 */
export default function Clients() {
  return (
    <>
      <Seo
        title="Untuk kantor — Kanata Indonesia"
        description="Proyek workspace kantor yang sudah Kanata kerjakan: konsultasi, produksi, dan pemasangan di tempat."
        path="/kantor"
      />

      <PageHero
        eyebrow="Untuk kantor"
        title="Satu lantai, atau satu kantor pusat."
        lead="Ukuran, jumlah dan jadwalnya dihitung dulu bareng kamu — bukan dipilih dari katalog ritel."
        image={clientsHeroImage}
      />

      <section className="bg-cream/60 py-14 sm:py-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <TrustedBy />
        </div>
      </section>

      <section className="py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHeading eyebrow="Cara kerjanya" title="Tiga tahap, dari ngobrol sampai dipakai." />

          <div className="mt-10 grid gap-5 sm:grid-cols-3">
            {officeProcess.map((step) => (
              <div key={step.step} className="rounded-2xl border border-ink/8 bg-white p-6">
                <div className="text-3xl font-medium text-brand">{step.step}</div>
                <h3 className="mt-3 text-lg font-medium text-ink">{step.title}</h3>
                <p className="mt-1.5 leading-relaxed text-ink/65">{step.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2">
            <span className="text-xs font-semibold uppercase tracking-[0.14em] text-ink/40">
              Sudah dipakai di
            </span>
            {industries.map((i) => (
              <span key={i} className="text-sm text-ink/70">
                {i}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-ink/8 bg-cream/50 py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHeading eyebrow="Proyek" title="Yang sudah kami kerjakan." />

          <div className="mt-10 space-y-14">
            {clients.map((c, i) => (
              <article
                key={c.slug}
                className="grid gap-8 lg:grid-cols-2 lg:items-center lg:gap-14"
              >
                <div className={i % 2 === 1 ? "lg:order-2" : undefined}>
                  <p className="text-xs font-semibold uppercase tracking-wider text-brand">
                    {c.industry}
                  </p>
                  <h3 className="mt-2 text-2xl font-medium text-ink">{c.name}</h3>
                  <p className="mt-1 text-sm text-ink/50">{c.location}</p>
                  <p className="mt-4 leading-relaxed text-ink/70">{c.summary}</p>
                </div>

                <div className={`grid grid-cols-2 gap-3 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                  {c.imageKeys.map((key, k) => (
                    <div
                      key={key}
                      className={`overflow-hidden rounded-2xl bg-white ${k === 0 ? "col-span-2 aspect-[16/10]" : "aspect-square"}`}
                    >
                      <img
                        src={clientImages[key]}
                        alt={`${c.name} — ${k + 1}`}
                        loading="lazy"
                        className="size-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHeading
            eyebrow="Model proyek"
            title="Tiga meja yang tidak dijual satuan."
            lead="Daya, Hadap dan Siku dibuat untuk kantor. Kami tidak memasangnya di keranjang seolah-olah bisa dibeli dalam dua klik."
          />

          <div className="mt-10 grid gap-5 sm:grid-cols-3">
            {projectDesks.map((p) => (
              <div key={p.slug} className="overflow-hidden rounded-2xl border border-ink/8 bg-white">
                <div className="aspect-[4/3] overflow-hidden bg-cream">
                  <img src={img(p.imageKey)} alt={p.name} loading="lazy" className="size-full object-cover" />
                </div>
                <div className="p-5">
                  <p className="text-xs font-medium uppercase tracking-wider text-ink/45">{p.type}</p>
                  <h3 className="mt-1 text-lg font-medium text-ink">{p.name}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-ink/65">{p.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 rounded-2xl border border-ink/8 bg-cream/60 p-7">
            <h2 className="text-lg font-medium text-ink">Mau bahas kantor kamu?</h2>
            <p className="mt-2 max-w-2xl leading-relaxed text-ink/65">
              Ceritakan berapa orang, berapa lantai, dan kapan mau jadi. Kami hitungkan.
            </p>
            <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Button asChild size="lg">
                <a href={contact.whatsappUrl} target="_blank" rel="noopener noreferrer" data-wa-placement="clients">
                  <MessageCircle />
                  Chat WhatsApp
                </a>
              </Button>
              <a
                href={partner.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-ink/60 transition-colors hover:text-brand"
              >
                Konstruksi bareng {partner.name}
                <ArrowUpRight className="size-3.5" />
              </a>
            </div>
            <p className="mt-4 text-sm text-ink/55">
              Kalau yang kamu cari cuma satu meja untuk di rumah,{" "}
              <Link to="/katalog" className="font-medium text-brand underline underline-offset-2">
                langsung beli di sini
              </Link>{" "}
              saja — lebih cepat.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
